import { Fragment, useCallback, useEffect, useState } from 'react';
import { Badge, Empty, Field, Note, Panel, Spinner, TableWrap } from '@wyocspay/ui';
import { dateTime, money, relative } from './format.js';
import {
  ApiError,
  api,
  type MerchantSubscriptionRow,
  type SubscriptionAnalysis,
  type SubscriptionApplicationRow,
} from './api.js';

/**
 * Абонаменти (пакети за освобождаване от такси).
 *
 * Търговец подава заявка от своя профил. Тук системата смята подова цена от
 * неговите СОБСТВЕНИ трансакции (средно за 3 месеца) и предлага цена над нея.
 * Одобряваш на цена НЕ по-ниска от подовата; създава се абонамент и линк за
 * плащане. Таксите падат чак когато търговецът плати първия месец.
 *
 *  - special - маха таксата на платформата (таксата на Stripe остава).
 *  - vipplus - маха всички такси; напълно възможно само ако акаунтът е на модел
 *    „application“ (виж бележката при vipplus).
 */

const TIER_LABEL: Record<string, string> = { special: 'Special · без такса на платформата', vipplus: 'VIP+ · без никакви такси' };
const STATUS_LABEL: Record<string, string> = {
  PENDING: 'ЧАКА ПРЕГЛЕД',
  INFO_REQUESTED: 'ПОИСКАНА ИНФО',
  APPROVED: 'ОДОБРЕНА',
  REJECTED: 'ОТХВЪРЛЕНА',
  CANCELLED: 'ОТТЕГЛЕНА',
};
const SUB_STATUS_LABEL: Record<string, string> = {
  PENDING_PAYMENT: 'ЧАКА ПЛАЩАНЕ',
  ACTIVE: 'АКТИВЕН',
  PAST_DUE: 'ПРОСРОЧЕН',
  CANCELLED: 'ПРЕКРАТЕН',
};

function statusTone(s: string): 'ok' | 'warn' | 'fail' | 'neutral' {
  if (s === 'APPROVED' || s === 'ACTIVE') return 'ok';
  if (s === 'PENDING' || s === 'INFO_REQUESTED' || s === 'PENDING_PAYMENT') return 'warn';
  if (s === 'REJECTED' || s === 'CANCELLED' || s === 'PAST_DUE') return 'fail';
  return 'neutral';
}

type Tab = 'waiting' | 'all' | 'APPROVED' | 'REJECTED';

export function SubscriptionsView({ onOpenMerchant }: { onOpenMerchant?: (id: string) => void }) {
  const [tab, setTab] = useState<Tab>('waiting');
  const [items, setItems] = useState<SubscriptionApplicationRow[]>([]);
  const [subs, setSubs] = useState<MerchantSubscriptionRow[]>([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);

  const load = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      const [apps, s] = await Promise.all([api.listSubscriptionApplications(), api.listSubscriptions()]);
      setItems(apps.applications);
      setPending(apps.pending);
      setSubs(s.subscriptions);
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => { if (document.visibilityState === 'visible') void load(true); }, 5000);
    return () => clearInterval(id);
  }, [load]);

  const rows = items.filter((a) => {
    if (tab === 'all') return true;
    if (tab === 'waiting') return a.status === 'PENDING' || a.status === 'INFO_REQUESTED';
    return a.status === tab;
  });
  const activeSubs = subs.filter((s) => s.status !== 'CANCELLED');

  const TABS: Array<[Tab, string]> = [
    ['waiting', `Чакат (${pending})`],
    ['all', 'Всички'],
    ['APPROVED', 'Одобрени'],
    ['REJECTED', 'Отхвърлени'],
  ];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Цената идва от анализ на трансакциите">
        Системата смята подова цена от собствените трансакции на търговеца (средно за 3 месеца) и
        предлага цена над нея. Можеш да я вдигаш, но никога под подовата. При „Одобри“ се създава
        абонамент и линк за плащане; таксите падат чак след като първият месец е платен.
      </Note>

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      {activeSubs.length > 0 ? (
        <Panel title={`Активни пакети · ${activeSubs.length}`} padded={false}>
          <TableWrap>
            <table className="wc-table">
              <thead><tr><th>Търговец</th><th>Пакет</th><th>Цена/месец</th><th>Статус</th><th>Подновяване</th><th /></tr></thead>
              <tbody>
                {activeSubs.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontSize: 13 }}>
                      {onOpenMerchant ? (
                        <button type="button" onClick={() => onOpenMerchant(s.merchant.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--wc-blue-ink)' }}>{s.merchant.name}</button>
                      ) : s.merchant.name}
                    </td>
                    <td style={{ fontSize: 13 }}>{TIER_LABEL[s.tier] ?? s.tier}</td>
                    <td className="wc-num" style={{ fontSize: 13 }}>{money(s.monthlyAmount, s.currency)}</td>
                    <td><Badge tone={statusTone(s.status)}>{SUB_STATUS_LABEL[s.status] ?? s.status}</Badge></td>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{s.currentPeriodEnd ? new Date(s.currentPeriodEnd).toLocaleDateString() : '-'}</td>
                    <td className="wc-right">
                      <button
                        type="button"
                        className="wc-btn wc-btn-sm wc-btn-danger"
                        onClick={async () => {
                          if (!confirm(`Да прекратя ли пакета на „${s.merchant.name}“? Таксите се възстановяват.`)) return;
                          try {
                            await api.cancelSubscription(s.id);
                            setMsg({ tone: 'warn', text: `Пакетът на „${s.merchant.name}“ е прекратен и таксите са възстановени.` });
                            await load();
                          } catch (err) {
                            setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
                          }
                        }}
                      >
                        Прекрати
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Panel>
      ) : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TABS.map(([key, label]) => (
          <button key={key} type="button" className={`wc-btn wc-btn-sm ${tab === key ? '' : 'wc-btn-secondary'}`} onClick={() => setTab(key)}>{label}</button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => void load()} disabled={loading}>Опресни</button>
        </div>
      </div>

      {loading ? <Spinner label="Зареждане…" /> : error ? <Note tone="danger">{error}</Note> : null}

      <Panel title={`Заявки за пакет · ${rows.length}`} padded={false}>
        {rows.length === 0 && !loading ? (
          <Empty>Няма заявки в тази категория.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead><tr><th>Търговец</th><th>Пакет</th><th>Подадена</th><th>Статус</th><th /></tr></thead>
              <tbody>
                {rows.map((a) => (
                  <ApplicationRow key={a.id} app={a} onChanged={load} onMsg={setMsg} onOpenMerchant={onOpenMerchant} />
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Panel>
    </div>
  );
}

function ApplicationRow({
  app,
  onChanged,
  onMsg,
  onOpenMerchant,
}: {
  app: SubscriptionApplicationRow;
  onChanged: () => Promise<void>;
  onMsg: (m: { tone: 'ok' | 'warn' | 'danger'; text: string } | null) => void;
  onOpenMerchant?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [analysis, setAnalysis] = useState<SubscriptionAnalysis | null>(null);
  const [loadingA, setLoadingA] = useState(false);
  const [amount, setAmount] = useState<string>('');
  const [mode, setMode] = useState<'info' | 'reject' | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [payUrl, setPayUrl] = useState<string | null>(null);

  const decided = app.status === 'APPROVED' || app.status === 'REJECTED' || app.status === 'CANCELLED';

  async function expand(): Promise<void> {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setMode(null);
    setText('');
    if (!decided) {
      setLoadingA(true);
      try {
        const res = await api.subscriptionApplication(app.id);
        setAnalysis(res.analysis);
        // Prefill with the suggested price (in major units for the input).
        if (res.analysis) setAmount((res.analysis.suggested / 100).toFixed(2));
      } finally {
        setLoadingA(false);
      }
    }
  }

  async function approve(): Promise<void> {
    if (!analysis) return;
    const minor = Math.round(Number(amount) * 100);
    if (!Number.isFinite(minor) || minor < analysis.floor) {
      onMsg({ tone: 'danger', text: `Цената трябва да е поне ${money(analysis.floor, analysis.currency)} (подовата от анализа).` });
      return;
    }
    setBusy(true);
    onMsg(null);
    try {
      const res = await api.approveSubscription(app.id, minor);
      setPayUrl(res.pay_url);
      onMsg({ tone: 'ok', text: `Пакетът на „${app.merchant.name}“ е одобрен на ${money(minor, analysis.currency)}/месец. Дай линка за плащане на търговеца - таксите падат след плащане.` });
      await onChanged();
    } catch (err) {
      onMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function submitInfo(): Promise<void> {
    if (text.trim().length < 4) return;
    setBusy(true);
    try {
      await api.requestInfoSubscription(app.id, text.trim());
      onMsg({ tone: 'warn', text: `Изпратено е запитване до ${app.merchant.email}.` });
      setOpen(false);
      await onChanged();
    } catch (err) {
      onMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function reject(): Promise<void> {
    setBusy(true);
    try {
      await api.rejectSubscription(app.id, text.trim() || undefined);
      onMsg({ tone: 'warn', text: `Заявката на „${app.merchant.name}“ е отхвърлена.` });
      setOpen(false);
      await onChanged();
    } catch (err) {
      onMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Fragment>
      <tr style={{ opacity: decided ? 0.72 : 1 }}>
        <td style={{ fontSize: 13 }}>
          {onOpenMerchant ? (
            <button type="button" onClick={() => onOpenMerchant(app.merchant.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--wc-blue-ink)' }}>{app.merchant.name}</button>
          ) : app.merchant.name}
          <div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>{app.merchant.email} · {app.merchant.country}</div>
        </td>
        <td style={{ fontSize: 13 }}>{TIER_LABEL[app.tier] ?? app.tier}</td>
        <td style={{ fontSize: 13, color: 'var(--wc-muted)' }} title={dateTime(app.createdAt)}>{relative(app.createdAt)}</td>
        <td><Badge tone={statusTone(app.status)}>{STATUS_LABEL[app.status] ?? app.status}</Badge></td>
        <td className="wc-right">
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => void expand()}>{open ? 'Скрий' : 'Прегледай'}</button>
        </td>
      </tr>

      {open ? (
        <tr>
          <td colSpan={5} style={{ background: 'var(--wc-blue-tint, var(--wc-grey-tint))' }}>
            <div style={{ padding: '10px 4px 14px', display: 'grid', gap: 12, maxWidth: 720 }}>
              {app.justification ? <Detail label="От търговеца">{app.justification}</Detail> : null}
              {app.volume ? <Detail label="Заявен оборот">{app.volume}</Detail> : null}

              {loadingA ? <Spinner label="Смятане на анализа…" /> : null}

              {!decided && analysis ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
                    <Stat label="Такса платформа / месец" value={money(analysis.monthlyPlatformFee, analysis.currency)} />
                    {app.tier === 'vipplus' ? <Stat label="Stripe такса / месец (оценка)" value={money(analysis.monthlyStripeFeeEst, analysis.currency)} /> : null}
                    <Stat label="Подова цена" value={money(analysis.floor, analysis.currency)} strong />
                    <Stat label="Предложена цена" value={money(analysis.suggested, analysis.currency)} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--wc-faint)' }}>
                    От трансакции {analysis.from} → {analysis.to}. Маржа {(analysis.marginBps / 100).toFixed(0)}%.
                    {analysis.fxNote ? ` ${analysis.fxNote}` : ''}
                  </div>
                  {app.tier === 'vipplus' && !analysis.vipplusFullyEffective ? (
                    <Note tone="warn" title="VIP+ и таксата на Stripe">
                      Акаунтът на този търговец е на модел „account“ - таксата на Stripe НЕ може да се
                      махне без ново онбордване (моделът се фиксира при създаване на акаунта). До
                      онбордване VIP+ ще действа като Special (маха само таксата на платформата).
                    </Note>
                  ) : null}

                  {mode === null ? (
                    <>
                      <Field label={`Месечна цена (${analysis.currency.toUpperCase()})`} hint={`Не по-ниска от ${money(analysis.floor, analysis.currency)}.`}>
                        <input className="wc-input" type="number" step="0.01" min={(analysis.floor / 100).toFixed(2)} value={amount} onChange={(e) => setAmount(e.target.value)} style={{ maxWidth: 200 }} />
                      </Field>
                      {payUrl ? (
                        <Note tone="ok" title="Линк за плащане">
                          Дай този линк на търговеца (или той го вижда в профила си):
                          <div style={{ marginTop: 6 }}><a href={payUrl} target="_blank" rel="noreferrer" className="wc-num" style={{ wordBreak: 'break-all' }}>{payUrl}</a></div>
                        </Note>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="wc-btn wc-btn-sm wc-btn-primary" disabled={busy} onClick={() => void approve()}>{busy ? 'Одобряване…' : 'Одобри и създай линк'}</button>
                          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy} onClick={() => { setMode('info'); setText(''); }}>Поискай информация</button>
                          <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" disabled={busy} onClick={() => { setMode('reject'); setText(''); }}>Отхвърли</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <Field label={mode === 'info' ? 'Каква информация ви трябва?' : 'Причина за отказ (по желание)'}>
                        <textarea className="wc-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} style={{ resize: 'vertical' }} />
                      </Field>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className={`wc-btn wc-btn-sm ${mode === 'reject' ? 'wc-btn-danger' : 'wc-btn-primary'}`} disabled={busy || (mode === 'info' && text.trim().length < 4)} onClick={() => (mode === 'info' ? void submitInfo() : void reject())}>
                          {busy ? 'Изпращане…' : mode === 'info' ? 'Изпрати' : 'Потвърди отхвърлянето'}
                        </button>
                        <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy} onClick={() => { setMode(null); setText(''); }}>Назад</button>
                      </div>
                    </>
                  )}
                </>
              ) : null}

              {decided ? (
                <div style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                  {app.adminAmount != null ? `Одобрена на ${money(app.adminAmount, app.currency)}/месец. ` : ''}
                  {app.reviewedAt ? `Решение: ${dateTime(app.reviewedAt)}` : ''}
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ padding: '8px 10px', background: 'var(--wc-panel, #fff)', borderRadius: 6, border: '1px solid var(--wc-border)' }}>
      <div style={{ fontSize: 11, color: 'var(--wc-faint)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div className="wc-num" style={{ fontSize: strong ? 16 : 14, fontWeight: strong ? 700 : 500, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'var(--wc-faint)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 13.5, marginTop: 3, whiteSpace: 'pre-wrap' }}>{children}</div>
    </div>
  );
}
